/**
 * Centralized barcode / QR payload parser for ePSF Individual Kit receive.
 * Does NOT treat Treatment/ACTIVE/PLACEBO as authoritative (Sponsor mapping owns that).
 */

function trim(value) {
  return String(value ?? "").trim();
}

function parseExpiry(raw) {
  const text = trim(raw);
  if (!text) return null;
  // YYYY-MM-DD or YYYYMMDD
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const compact = text.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) return `${compact[1]}-${compact[2]}-${compact[3]}`;
  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = text.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    const dd = dmy[1].padStart(2, "0");
    const mm = dmy[2].padStart(2, "0");
    return `${dmy[3]}-${mm}-${dd}`;
  }
  const dt = new Date(text);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  return null;
}

/**
 * @param {string} rawScan
 * @returns {{
 *   kitNumber: string|null,
 *   batchNumber: string|null,
 *   expiryDate: string|null,
 *   format: 'simple'|'structured'|'unknown',
 *   raw: string
 * }}
 */
export function parseEpsfBarcodePayload(rawScan) {
  const raw = trim(rawScan);
  const empty = {
    kitNumber: null,
    batchNumber: null,
    expiryDate: null,
    format: "unknown",
    raw,
  };
  if (!raw) return empty;

  // Structured: KIT=... LOT=... EXP=... (newline, semicolon, or pipe separated)
  if (/KIT\s*=/i.test(raw) || /LOT\s*=/i.test(raw) || /EXP\s*=/i.test(raw)) {
    const parts = {};
    raw.split(/[\r\n;|]+/).forEach((segment) => {
      const m = trim(segment).match(/^([A-Za-z_]+)\s*=\s*(.+)$/);
      if (!m) return;
      parts[m[1].toUpperCase()] = trim(m[2]);
    });
    const kitNumber = parts.KIT || parts.KITNUMBER || parts.KIT_NO || null;
    const batchNumber = parts.LOT || parts.BATCH || parts.BATCHLOT || null;
    const expiryDate = parseExpiry(parts.EXP || parts.EXPIRY || parts.EXPIRE || "");
    return {
      kitNumber: kitNumber ? kitNumber.slice(0, 100) : null,
      batchNumber: batchNumber ? batchNumber.slice(0, 100) : null,
      expiryDate,
      format: "structured",
      raw,
    };
  }

  // JSON object
  if (raw.startsWith("{")) {
    try {
      const obj = JSON.parse(raw);
      const kitNumber = trim(
        obj.KitNumber || obj.kitNumber || obj.KIT || obj.kit || "",
      );
      const batchNumber = trim(
        obj.BatchNumber || obj.batchNumber || obj.LOT || obj.Lot || "",
      );
      const expiryDate = parseExpiry(
        obj.ExpiryDate || obj.expiryDate || obj.EXP || obj.Exp || "",
      );
      if (kitNumber) {
        return {
          kitNumber: kitNumber.slice(0, 100),
          batchNumber: batchNumber ? batchNumber.slice(0, 100) : null,
          expiryDate,
          format: "structured",
          raw,
        };
      }
    } catch {
      /* fall through */
    }
  }

  // Simple: entire payload is KitNumber (reject if contains whitespace-heavy multi-token without KIT=)
  if (!/\s/.test(raw) && raw.length <= 100) {
    return {
      kitNumber: raw,
      batchNumber: null,
      expiryDate: null,
      format: "simple",
      raw,
    };
  }

  // First non-empty token as kit when pasted as single line with spaces
  const first = raw.split(/\s+/)[0];
  if (first && first.length <= 100) {
    return {
      kitNumber: first,
      batchNumber: null,
      expiryDate: null,
      format: "simple",
      raw,
    };
  }

  return empty;
}

export default parseEpsfBarcodePayload;
